import React, { FC, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import ReactInputMask from 'react-input-mask';
import { useHistory, useParams } from 'react-router-dom';
import { useGroupPaymentCardInfo } from 'customHooks/useGroupPaymentCardInfo';
import { useSetRecoilState } from 'recoil';
import { CREATE_GROUP_PAYMENT_CARD } from 'graphql/service';
import { reloadTrips } from 'states/CommonStates/atoms';
import { useGlobalAlerts } from 'customHooks/useGlobalAlerts';
import FormInputField from 'components/FormInputField';
import { colors } from 'constants/colors';
import { detectCardType, isAmexCardNumber } from 'utils/util';
import { useGoogleAutocomplete } from 'customHooks/useGoogleAutocomplete';
import { validateBillingAddress } from 'utils/billingAddressValidation';

import {
  creditCardMask,
  expireDateMask,
  cvvMask,
  amexCvvMask,
} from '../../../../../CheckOutScreen/components/inputMask/inputMasks';

import {
  EditCreditCardContentContainer,
  EditCreditCardFormContainer,
  EditCreditCardButtonRow,
  EditCreditCardSaveButton,
} from './style';


const debug = false;

const EditCreditCard: FC = () => {
  const history = useHistory();

  const { showGlobalError, showGlobalMessage } = useGlobalAlerts();
  const setReloadTrips = useSetRecoilState(reloadTrips);


  const { groupId } = useParams<{ groupId: string }>();

  const {
    holdAmount,
    cardLastFourDigits
  } = useGroupPaymentCardInfo(groupId);

  if (debug) {
    console.log('[EditCreditCard]groupId = ', groupId);
  }


  const cardNumberRef = useRef<HTMLInputElement>(null);
  const cvvRef = useRef<HTMLInputElement>(null);
  const expiryRef = useRef<HTMLInputElement>(null);

  const [cardHolderName, setCardHolderName] = useState('');
  const [creditCardNumber, setCreditCardNumber] = useState('');
  const [cardCVV, setCardCVV] = useState('');
  const cardNumberDigits = creditCardNumber.replace(/\D/g, '');
  const isAmex = isAmexCardNumber(cardNumberDigits);
  const cvvLength = isAmex ? 4 : 3;
  const cvvTouchedRef = useRef(false);
  const [cardExpirationDate, setCardExpirationDate] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    fullAddress: '',
    streetAddress: '',
    country: '',
    province: '',
    city: '',
    postalCode: '',
  });


  const autocompleteRef = useGoogleAutocomplete((parsedAddress: any) => {
    const addressValidation = validateBillingAddress(parsedAddress);
  
    setBillingAddress({
      fullAddress:
        parsedAddress.fullAddress ||
        parsedAddress.streetAddress ||
        '',
      streetAddress: parsedAddress.streetAddress || '',
      country: parsedAddress.country || '',
      province: parsedAddress.province || '',
      city: parsedAddress.city || '',
      postalCode: parsedAddress.postalCode || '',
    });
  
    setErrors((prev) => ({
      ...prev,
      billingAddress: !addressValidation.isValid,
    }));
  
    setFieldErrorMessages((prev) => ({
      ...prev,
      ADDRESS: addressValidation.errorMessage,
    }));
  });


  const [fieldErrorMessages, setFieldErrorMessages] = useState<Record<string, string>>({});
  const clearFieldErrorMessage = (field: string) => {
    setFieldErrorMessages((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const handleBillingAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    clearFieldErrorMessage('ADDRESS');
    setBillingAddress({
      fullAddress: value,
      streetAddress: value,
      country: '',
      province: '',
      city: '',
      postalCode: '',
    });

    if (errors.billingAddress) {
      setErrors((prev) => ({ ...prev, billingAddress: false }));
    }
  };

  const [errors, setErrors] = useState({
    cardHolderName: false,
    creditCardNumber: false,
    cardCVV: false,
    cardExpirationDate: false,
    billingAddress: false,
  });




  const getCardValidationError = (error: any) => {
    const graphQLError = error?.graphQLErrors?.[0];
    const extensions = graphQLError?.extensions || {};

    return {
      message: graphQLError?.message || 'Payment card update failed. Please try again.',
      code: extensions.code,
      errorCode: extensions.errorCode,
      field: extensions.field,
      validationErrors: extensions.validationErrors || [],
    };
  };

  const getMissingBillingAddressFields = (message: string) => {
    const fieldLabels = [
      { key: 'streetAddress', label: 'street address' },
      { key: 'city', label: 'city' },
      { key: 'province', label: 'province' },
      { key: 'postalCode', label: 'postal code' },
      { key: 'country', label: 'country' },
    ];

    return fieldLabels
      .filter(({ key }) => {
        const fieldRegex = new RegExp(
          `${key}:\\s*${key}\\s+should\\s+not\\s+be\\s+empty`,
          'i'
        );

        return fieldRegex.test(message);
      })
      .map(({ label }) => label);
  };

  const getMissingBillingAddressMessage = (message: string) => {
    const missingFields = getMissingBillingAddressFields(message);

    if (missingFields.length === 0) {
      return 'Please select a complete billing address.';
    }

    return `Billing address is missing: ${missingFields.join(', ')}.`;
  };

  const normalizeCardValidationField = (field?: string) => {
    switch (field) {
      case 'CARD_NUMBER':
      case 'CVV':
      case 'EXPIRY':
      case 'HOLDER_NAME':
        return field;

      case 'ADDRESS':
      case 'STREET_ADDRESS':
      case 'STREETADDRESS':
      case 'ZIPCODE':
      case 'POSTAL_CODE':
      case 'POSTALCODE':
      case 'CITY':
      case 'PROVINCE':
      case 'COUNTRY':
        return 'ADDRESS';

      default:
        return field;
    }
  };



  const applyCardFieldError = (field: string | undefined, message: string) => {
    const normalizedField = normalizeCardValidationField(field);

    if (!normalizedField || normalizedField === 'NONE') {
      showGlobalError(message);
      return;
    }

    setFieldErrorMessages((prev) => ({
      ...prev,
      [normalizedField]: message,
    }));

    switch (normalizedField) {
      case 'CARD_NUMBER':
        setErrors((prev) => ({ ...prev, creditCardNumber: true }));
        break;

      case 'EXPIRY':
        setErrors((prev) => ({ ...prev, cardExpirationDate: true }));
        break;

      case 'CVV':
        setErrors((prev) => ({ ...prev, cardCVV: true }));
        break;

      case 'HOLDER_NAME':
        setErrors((prev) => ({ ...prev, cardHolderName: true }));
        break;

      case 'ADDRESS':
        setErrors((prev) => ({ ...prev, billingAddress: true }));
        break;

      default:
        showGlobalError(message);
        break;
    }
  };


  const applyBillingAddressInputValidationError = (message: string) => {
    const lowerMessage = message.toLowerCase();

    const hasBillingAddressError =
      lowerMessage.includes('streetaddress') ||
      lowerMessage.includes('street address') ||
      lowerMessage.includes('city') ||
      lowerMessage.includes('province') ||
      lowerMessage.includes('postalcode') ||
      lowerMessage.includes('postal code') ||
      lowerMessage.includes('country');

    if (!hasBillingAddressError) {
      return false;
    }

    applyCardFieldError(
      'ADDRESS',
      getMissingBillingAddressMessage(message)
    );

    return true;
  };


  const [createGroupPaymentCard, { loading }] = useMutation(CREATE_GROUP_PAYMENT_CARD, {
    onCompleted: () => {
      setReloadTrips(true);
      showGlobalMessage('Payment card updated successfully.');
      history.push('/account/billing/');
    },
    onError: (error) => {
      if (debug) {
        console.log('createGroupPaymentCard error:', error);
      }

      setFieldErrorMessages({});

      const {
        message,
        code,
        field,
        validationErrors,
      } = getCardValidationError(error);

      if (code === 'CARD_VALIDATION_ERROR') {
        if (validationErrors.length > 0) {
          validationErrors.forEach((validationError: any) => {
            applyCardFieldError(
              validationError.field,
              validationError.message || message
            );
          });

          return;
        }

        applyCardFieldError(field, message);
        return;
      }

      if (code === 'InputValidationError') {
        const wasAppliedToField = applyBillingAddressInputValidationError(message);

        if (wasAppliedToField) {
          return;
        }

        showGlobalError(message || 'Payment card update failed. Please try again.');
        return;
      }

      showGlobalError(message || 'Payment card update failed. Please try again.');
    },
  });

  const focusNextInput = (ref: React.RefObject<HTMLInputElement>) => {
    ref.current?.focus();
  };

  const handleCardNumberChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    const digits = value.replace(/\D/g, '');
    const nextIsAmex = isAmexCardNumber(digits);
    const nextCardLength = nextIsAmex ? 15 : 16;
    const nextCvvLength = nextIsAmex ? 4 : 3;
    const type = digits.length === nextCardLength ? detectCardType(digits) : '';

    clearFieldErrorMessage('CARD_NUMBER');
    setCreditCardNumber(value);
    setCardCVV((prev) => prev.slice(0, nextCvvLength));
    setErrors((prev) => ({
      ...prev,
      creditCardNumber: digits.length >= nextCardLength && !type,
    }));

    if (nextCvvLength !== cvvLength) {
      clearFieldErrorMessage('CVV');
      setErrors((prev) => ({
        ...prev,
        cardCVV: (cvvTouchedRef.current || prev.cardCVV) &&
          !new RegExp('^[0-9]{' + nextCvvLength + '}$').test(cardCVV.slice(0, nextCvvLength)),
      }));
    }

    if (type && cardNumberDigits.length < nextCardLength &&
        digits.startsWith(cardNumberDigits)) {
      focusNextInput(cvvRef);
    }
  };

  const handleCVVChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, cvvLength);
    if (value === cardCVV) {
      return;
    }
    clearFieldErrorMessage('CVV');
    if (value || cardCVV) {
      cvvTouchedRef.current = true;
    }
    setCardCVV(value);

    if (errors.cardCVV) {
      setErrors((prev) => ({ ...prev, cardCVV: false }));
    }

    if (value.length === cvvLength && cardCVV.length < cvvLength &&
        value.startsWith(cardCVV)) {
      focusNextInput(expiryRef);
    }
  };

  const handleExpiryChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    clearFieldErrorMessage('EXPIRY');
    setCardExpirationDate(value);

    if (errors.cardExpirationDate) {
      setErrors((prev) => ({ ...prev, cardExpirationDate: false }));
    }
  };


  const handleSave = () => {
    cvvTouchedRef.current = true;
    const addressValidation =
      validateBillingAddress(billingAddress);
  
    const nextErrors = {
      cardHolderName: cardHolderName.trim().length < 3,
      creditCardNumber: isAmex
        ? detectCardType(cardNumberDigits) !== 'amex'
        : cardNumberDigits.length < 16,
      cardCVV: !new RegExp('^[0-9]{' + cvvLength + '}$').test(cardCVV),
      cardExpirationDate: cardExpirationDate.length < 5,
      billingAddress: !addressValidation.isValid,
    };
  
    setErrors(nextErrors);
  
    setFieldErrorMessages((prev) => ({
      ...prev,
      HOLDER_NAME: '',
      CARD_NUMBER: '',
      CVV: '',
      EXPIRY: '',
      ADDRESS: addressValidation.errorMessage,
    }));
  
    const hasErrors = Object.values(nextErrors).some(Boolean);
  
    if (hasErrors) {
      return;
    }
  
    const input = {
      cardHolderName: cardHolderName.trim(),
      creditCardNumber: creditCardNumber.replace(/\s/g, ''),
      cardCVV,
      cardExpirationDate:
        cardExpirationDate.replace(/[\s/]/g, ''),
      groupId: String(groupId),
      isPrimary: true,
      isOTAPrepayment: false,
      streetAddress: billingAddress.streetAddress.trim(),
      city: billingAddress.city.trim(),
      province: billingAddress.province.trim(),
      postalCode: billingAddress.postalCode.trim(),
      country: billingAddress.country.trim(),
    };
  
    if (debug) {
      console.log('create group payment card input:', input);
    }
  
    createGroupPaymentCard({
      variables: {
        input,
      },
    });
  };

  // const handleCancel = () => {
  //   history.push('/account/billing/');
  // };

  return (
    <EditCreditCardContentContainer>
      <EditCreditCardFormContainer>
        <p className="description">
          Your booking is currently linked to your card ending in: {cardLastFourDigits}. There is a hold placed on this card for a total of ${holdAmount}. <br />
          If you wish to change your card, we will place the same hold on the new card and then release the hold on your current card.
        </p>

        <FormInputField
          className="editCreditCardInput"
          label="NAME ON THE CARD"
          labelWeight="400"
          isRequired={true}
          value={cardHolderName}
          error={!!fieldErrorMessages.HOLDER_NAME || errors.cardHolderName}
          errorMessage={fieldErrorMessages.HOLDER_NAME || 'Please enter a valid full name'}
          borderColor={colors.signInBorderColor}
          inputHeight="48px"
          onChange={(e) => {
            clearFieldErrorMessage('HOLDER_NAME');
            setCardHolderName(e.target.value);

            if (errors.cardHolderName) {
              setErrors((prev) => ({ ...prev, cardHolderName: false }));
            }
          }}
          onBlur={(e) => {
            setErrors((prev) => ({
              ...prev,
              cardHolderName: e.target.value.trim().length < 3,
            }));
          }}
        />

        <ReactInputMask
          mask={creditCardMask.mask}
          maskPlaceholder=""
          value={creditCardNumber}
          onChange={handleCardNumberChange}
          onBlur={(e) => {
            setErrors((prev) => ({
              ...prev,
              creditCardNumber: isAmexCardNumber(e.target.value)
                ? detectCardType(e.target.value.replace(/\D/g, '')) !== 'amex'
                : e.target.value.replace(/\D/g, '').length < 16,
            }));
          }}
        >
          <FormInputField
            className="editCreditCardInput"
            inputRef={cardNumberRef}
            label="CARD NUMBER"
            labelWeight="400"
            isRequired={true}
            placeholder={isAmex ? 'xxxx xxxx xxxx xxx' : 'xxxx xxxx xxxx xxxx'}
            maxLength={19}
            error={!!fieldErrorMessages.CARD_NUMBER || errors.creditCardNumber}
            errorMessage={fieldErrorMessages.CARD_NUMBER || 'Please enter a valid credit card number'}
            borderColor={colors.signInBorderColor}
            inputHeight="48px"
          />
        </ReactInputMask>

        <div className="cardDetailsRow">
          <ReactInputMask
            mask={isAmex ? amexCvvMask.mask : cvvMask.mask}
            maskPlaceholder=""
            value={cardCVV}
            onChange={handleCVVChange}
            onBlur={(e) => {
              cvvTouchedRef.current = true;
              setErrors((prev) => ({
                ...prev,
                cardCVV: !new RegExp('^[0-9]{' + cvvLength + '}$').test(e.target.value),
              }));
            }}
          >
            <FormInputField
              className="editCreditCardInput"
              inputRef={cvvRef}
              label="CVV"
              labelWeight="400"
              isRequired={true}
              placeholder={isAmex ? 'xxxx' : 'xxx'}
              error={!!fieldErrorMessages.CVV || errors.cardCVV}
              errorMessage={fieldErrorMessages.CVV || 'Please enter a valid CVV'}
              borderColor={colors.signInBorderColor}
              inputHeight="48px"
            />
          </ReactInputMask>

          <ReactInputMask
            mask={expireDateMask.mask}
            maskPlaceholder=""
            value={cardExpirationDate}
            onChange={handleExpiryChange}
            onBlur={(e) => {
              setErrors((prev) => ({
                ...prev,
                cardExpirationDate: e.target.value.length < 5,
              }));
            }}
          >
            <FormInputField
              className="editCreditCardInput"
              inputRef={expiryRef}
              label="EXPIRY (MM/YY)"
              labelWeight="400"
              isRequired={true}
              placeholder="mm/yy"
              error={!!fieldErrorMessages.EXPIRY || errors.cardExpirationDate}
              errorMessage={fieldErrorMessages.EXPIRY || 'Please enter a valid expiry date'}
              borderColor={colors.signInBorderColor}
              inputHeight="48px"
            />
          </ReactInputMask>
        </div>

        <FormInputField
          className="editCreditCardInput editCreditCardInputBillingAddress"
          inputRef={autocompleteRef}
          label="BILLING ADDRESS"
          labelWeight="400"
          isRequired={true}
          value={billingAddress.fullAddress}
          error={!!fieldErrorMessages.ADDRESS || errors.billingAddress}
          errorMessage={
            fieldErrorMessages.ADDRESS ||
            'Please enter a valid billing address'
          }
          borderColor={colors.signInBorderColor}
          inputHeight="48px"
          onChange={handleBillingAddressChange}
          onBlur={(e) => {
            setErrors((prev) => ({
              ...prev,
              billingAddress: e.target.value.trim().length < 3,
            }));
          }}
        />
        <EditCreditCardButtonRow>
          <EditCreditCardSaveButton onClick={handleSave}>
            {loading ? 'Saving...' : 'Save Credit Card'}
          </EditCreditCardSaveButton>

          {/* <EditCreditCardCancelButton onClick={handleCancel}>
            Cancel
          </EditCreditCardCancelButton> */}
        </EditCreditCardButtonRow>
      </EditCreditCardFormContainer>
    </EditCreditCardContentContainer>
  );
};

export default EditCreditCard;